import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { updateBug } from "../../api/bug.service";
import { fetchBug } from "../../app/bugs/bugs.slice";
import dayjs from "dayjs";
import AddBugModal from "./components/AddBugModal";
import ReplyBugModal from "./components/ReplyBugModal";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import SearchInput from "../../components/SearchInput";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";

const BugsReports = () => {
  const { t } = useTranslation();
  const [visibleModalAddBug, setVisibleModalAddBug] = useState(false);
  const [visibleModalReplyBug, setVisibleModalReplyBug] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const { bugs, user } = useSelector((state) => state);

  const handleDeleteBug = async (bug) => {
    const json = { bugType: bug?.bugType, lang: bug?.lang, _id: bug?._id, active: false };
    const resp = await updateBug(json);
    if (resp?.status === 200) {
      toast.success(t("toast.bugDeleted"));
      dispatch(fetchBug());
    } else {
      toast.error(t("toast.bugNotDeleted") + (resp?.response?.data?.message ? ` (${resp.response.data.message})` : ""));
    }
  };

  const bugList = bugs?.value || [];

  const filteredBugs = search
    ? bugList.filter((b) => {
        const s = search.toLowerCase();
        return (
          b?.card?.meaning?.character?.toLowerCase()?.includes(s) ||
          b?.card?.meaning?.trans?.toLowerCase()?.includes(s) ||
          b?.reason?.toLowerCase()?.includes(s) ||
          b?.bugType?.toLowerCase()?.includes(s)
        );
      })
    : bugList;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <AddBugModal visible={visibleModalAddBug} setVisible={setVisibleModalAddBug} />
      <ReplyBugModal visible={visibleModalReplyBug} setVisible={setVisibleModalReplyBug} selectedBug={selectedBug} />

      <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>{t("pages.bugsReports")}</Typography>
            <Chip label={filteredBugs.length} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchInput
              placeholder={t("bugs.searchBug")}
              value={search}
              onChange={setSearch}
              onClear={() => setSearch("")}
              size="medium"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setVisibleModalAddBug(true)}>
              {t("bugs.addBug")}
            </Button>
          </Stack>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("bugs.char")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("bugs.lang")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("bugs.errorType")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("bugs.reason")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("users.reportBy")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("common.status")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("common.date")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }} align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...filteredBugs].reverse().map((bug, index) => (
                <TableRow key={bug?._id || index} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{bug?.card?.meaning?.character}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={bug?.lang?.toUpperCase()} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{bug?.bugType}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" color="text.secondary" noWrap title={bug?.reason}>{bug?.reason}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{bug?.createdBy?.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bug?.active ? t("bugs.pending") : t("bugs.solved")}
                      size="small"
                      color={bug?.active ? "warning" : "success"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {dayjs(bug?.date).format("DD MMM YYYY, HH:mm")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => { setVisibleModalReplyBug(true); setSelectedBug(bug); }}>
                        <ReplyIcon fontSize="small" />
                      </IconButton>
                      {user?.value?.role === "ADMIN" && (
                        <IconButton size="small" color="primary" onClick={() => window.open(`/card/edit/${bug?.card?._id}`, "_blank")}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDeleteBug(bug)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BugsReports;
